// @flow
import React, { PureComponent } from "react";
import type { Dispatch } from "redux";
import { connect } from "react-redux";
import tvgConf from "@tvg/conf";
import { get, findIndex, attempt, isError } from "lodash";

import type {
  RaceInfo,
  RaceProgram,
  RaceVideoFeedback,
  Video
} from "@tvg/types/Race";
import type { UserOptedInPromos } from "@tvg/types/User";
import RaceCardHeader from "@tvg/atomic-ui/_templates/RaceCardHeader";
import mediator from "@tvg/mediator";
import buildRaceUrl, {
  buildLiveUrl,
  addParamToURL,
  isFullURL
} from "@tvg/formatter/url";
import RaceHeaderUtils from "@tvg/utils/RaceHeaderUtils";
import performanceUtils from "@tvg/utils/performanceUtils";
import {
  setRaceSelectorModalStateAction,
  saveVideoFeedback,
  type Actions as ProgramActions
} from "@tvg/shared-actions/ProgramActions";
import {
  resetBetting,
  type Actions as BetActions
} from "@tvg/shared-actions/BetActions";
import isMobile, { openExternalLink } from "@tvg/utils/mobileUtils";
import getOptedInPromos from "@tvg/utils/PromoUtils";
import {
  showVideo,
  closeVideo,
  type Actions as VideoActions
} from "../actions/video";
import {
  togglePoolsNInfoModal,
  type Actions as poolsNInfoAction
} from "../actions/poolsNInfo";
import RaceVideo from "../RaceVideoComponent";

export type Props = {
  dispatch: Dispatch<
    BetActions | ProgramActions | VideoActions | poolsNInfoAction
  >,
  currentRace: RaceProgram,
  races: RaceProgram[],
  feedback: RaceVideoFeedback,
  video: Video,
  isVideoButtonShown: boolean,
  isHeaderRaceNavigationShown: boolean,
  isUserLogged: boolean,
  userBalance: number,
  isBalanceShown: string,
  channel: string,
  isControlsDisabled: boolean,
  isSticky: boolean,
  hasPromoBadge: boolean,
  optedInPromos: UserOptedInPromos,
  enableRaceChanges: boolean,
  accountNumber: number,
  enablePromosStoryblok: boolean,
  oldPromosURLs: string,
  openVideoFromMyBets: boolean,
  useIsPromoTagShownFlag: boolean
};

export type State = {
  changedTrack: boolean
};

export class RaceHeaderComponent extends PureComponent<Props, State> {
  static defaultProps = {
    races: [],
    optedInPromos: {},
    isUserLogged: false,
    userBalance: 0,
    isBalanceShown: "0",
    channel: "",
    isControlsDisabled: false,
    isSticky: false,
    isVideoButtonShown: false,
    isHeaderRaceNavigationShown: false,
    hasPromoBadge: true,
    enableRaceChanges: false,
    accountNumber: 0,
    enablePromosStoryblok: false,
    oldPromosURLs: "[]",
    openVideoFromMyBets: false,
    useIsPromoTagShownFlag: false
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      changedTrack: false
    };
  }

  componentDidMount() {
    this.saveVideoFeedback();

    if (
      (this.props.channel && this.props.isUserLogged) ||
      this.props.openVideoFromMyBets
    ) {
      this.props.dispatch(showVideo());
    } else {
      this.props.dispatch(closeVideo());
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (
      this.props.currentRace.track !== undefined &&
      prevProps.currentRace.track !== undefined
    )
      this.onChangeTrack(
        this.props.currentRace.track.trackCode,
        prevProps.currentRace.track.trackCode
      );

    if (
      this.props.openVideoFromMyBets !== prevProps.openVideoFromMyBets &&
      this.props.openVideoFromMyBets
    )
      this.props.dispatch(showVideo());

    if (this.props.feedback !== prevProps.feedback) this.saveVideoFeedback();
  }

  onChangeTrack(currentTrackCode: string, prevTrackCode: string) {
    if (currentTrackCode !== prevTrackCode) {
      this.setState({
        changedTrack: true
      });
    }
  }

  onVideoClick = () => {
    const { dispatch, isUserLogged } = this.props;

    if (!isUserLogged) {
      mediator.base.dispatch({
        type: "OPEN_LOGIN",
        payload: {
          callback: (error, success) => {
            if (success) {
              dispatch(showVideo());
            }
          },
          triggerAction: "race_header_video_click"
        }
      });
    } else {
      this.setState({
        changedTrack: false
      });
      dispatch(showVideo());
    }
  };

  onPromoClick = (event: Event, isExternal: boolean, raceInfo: RaceInfo) => {
    if (this.props.hasPromoBadge) {
      if (isExternal && isMobile(tvgConf().product)) {
        event.stopPropagation();
        event.preventDefault();
        openExternalLink(
          get(raceInfo, "promo.promoPath", "https://www.tvg.com/promos")
        );
      }
      mediator.base.dispatch({
        type: "PROMO_SIGNAGE:CLICK",
        payload: {
          module: "PP Header",
          destinationUrl: get(raceInfo, "promo.promoPath"),
          isOptedIn: get(raceInfo, "promo.isOptedIn")
        }
      });
    }
  };

  isPromoExternal = ({
    race,
    oldPromosURLs
  }: {
    race: RaceInfo,
    oldPromosURLs: string[]
  }) => {
    const { promo } = race;
    const promoPath = get(race, "promo.promoPath", "");
    let isExternal: boolean = false;

    if (promo) {
      isExternal = isFullURL(promoPath);
      if (isExternal) {
        const urlObject = new URL(promoPath);
        const isOldPromotion = oldPromosURLs.some((oldPromoURL) =>
          oldPromoURL.includes(urlObject.pathname)
        );
        // full URL is not included in old promos, must be parsed
        if (!isOldPromotion) {
          isExternal = false;
          promo.promoPath = urlObject.pathname;
        }
      } else {
        const isOldPromotion = oldPromosURLs.some((oldPromoURL) =>
          oldPromoURL.includes(promoPath)
        );
        // not a full URL but it's an old promo, must be changed to full url
        if (isOldPromotion) {
          isExternal = true;
          promo.promoPath = `${window.location.origin + promo.promoPath}`;
        }
      }
    }
    return isExternal;
  };

  saveVideoFeedback = () => {
    const { feedback, channel } = this.props;

    this.props.dispatch(
      saveVideoFeedback(channel ? "AVAILABLE_NO_REPLAY" : feedback)
    );
  };

  openRaceSelector = () => {
    // gtm event - open race selector
    mediator.base.dispatch({
      type: "PPM_OPEN_RACE_NAVIGATION",
      payload: {
        trackName: get(this.props, "currentRace.track.trackName"),
        raceNumber: get(this.props, "currentRace.raceNumber"),
        module: "PP Header"
      }
    });
    this.props.dispatch(setRaceSelectorModalStateAction(true));
  };

  openPoolsNInfo = () => {
    this.props.dispatch(togglePoolsNInfoModal(true));
    mediator.base.dispatch({
      type: "POOLS_INFO:OPEN_CLOSE",
      payload: {
        open: true,
        module: "PP Header"
      }
    });
    performanceUtils.register("page_load");
  };

  renderVideo = () => {
    const { currentRace, channel } = this.props;
    const stream = get(currentRace, "video.streams[0]", "");

    return (
      <RaceVideo
        currentRace={currentRace}
        stream={stream}
        channel={channel}
        feedback={this.props.feedback}
        openVideoFromMyBets={this.props.openVideoFromMyBets}
      />
    );
  };

  renderHeader = () => {
    const { currentRace, feedback, channel } = this.props;

    // $FlowFixMe
    const raceInfo: RaceInfo = {
      mtp: get(currentRace, "mtp"),
      raceNumber: get(currentRace, "raceNumber"),
      trackAbbr: get(currentRace, "track.trackCode"),
      trackName: get(currentRace, "track.shortName"),
      status: get(currentRace, "status.code"),
      postTime: get(currentRace, "postTime"),
      isGreyhound: get(currentRace, "type.code") === "G",
      promo:
        this.props.hasPromoBadge &&
        getOptedInPromos(
          get(currentRace, "promos", []),
          this.props.optedInPromos,
          this.props.isUserLogged
        )
    };

    const raceData = {
      highlightedDescription: get(currentRace, "highlighted.description", ""),
      importanceLevel:
        get(this.props, "currentRace.highlighted.style") === "Normal"
          ? "0"
          : "1",
      raceInfo: RaceHeaderUtils.createRaceDataForHeader(
        currentRace,
        this.props.enableRaceChanges
      )
    };

    const currentRaceIndex = findIndex(
      this.props.races,
      (r) => get(r, "id") === get(this.props, "currentRace.id")
    );
    const previousRace = this.props.races[currentRaceIndex - 1];
    const nextRace = this.props.races[currentRaceIndex + 1];

    let nextRaceUrl = "";
    let previousRaceUrl = "";

    if (channel) {
      nextRaceUrl = nextRace
        ? buildLiveUrl(
            get(nextRace, "track.trackCode", ""),
            get(nextRace, "track.trackName", ""),
            get(nextRace, "raceNumber", ""),
            channel
          )
        : "";

      previousRaceUrl = previousRace
        ? buildLiveUrl(
            get(previousRace, "track.trackCode", ""),
            get(previousRace, "track.trackName", ""),
            get(previousRace, "raceNumber", ""),
            channel
          )
        : "";
    } else {
      nextRaceUrl = nextRace
        ? buildRaceUrl(
            get(nextRace, "track.trackCode", ""),
            get(nextRace, "track.trackName", ""),
            get(nextRace, "raceNumber", "")
          )
        : "";

      previousRaceUrl = previousRace
        ? buildRaceUrl(
            get(previousRace, "track.trackCode", ""),
            get(previousRace, "track.trackName", ""),
            get(previousRace, "raceNumber", "")
          )
        : "";
    }
    const newNextRaceUrl = addParamToURL(
      nextRaceUrl,
      "arrowNavigation",
      "true"
    );
    const newPreviousRaceUrl = addParamToURL(
      previousRaceUrl,
      "arrowNavigation",
      "true"
    );

    let oldPromos = attempt(JSON.parse, this.props.oldPromosURLs);

    if (isError(oldPromos)) {
      oldPromos = [];
    }

    const isExternal = this.isPromoExternal({
      race: raceInfo,
      oldPromosURLs: oldPromos
    });

    return (
      <RaceCardHeader
        videoHeight={get(this.props, "fixWagerSelector.fixedOffset", 0)}
        dismissControllButtons={this.props.isVideoButtonShown}
        dismissRaceNavigation={this.props.isHeaderRaceNavigationShown}
        raceData={raceData}
        // $FlowFixMe
        raceInfo={raceInfo}
        oldPromosURLs={oldPromos}
        openRaceNavigation={this.openRaceSelector}
        openPoolsNInfo={this.openPoolsNInfo}
        enablePromosStoryblok={this.props.enablePromosStoryblok}
        quickDeposit={{
          balance: this.props.userBalance,
          isBalanceShown: this.props.isBalanceShown !== "0",
          url: `?race=${get(
            this.props,
            "currentRace.raceNumber",
            "1"
          )}#deposit`,
          onClick: () =>
            mediator.base.dispatch({
              type: "DEPOSITS_OPEN_QUICK",
              payload: { module: "Video" }
            })
        }}
        videoButton={{
          feedback: channel ? "AVAILABLE_NO_REPLAY" : feedback,
          small: false,
          onClick: this.onVideoClick
        }}
        isControlsDisabled={this.props.isControlsDisabled}
        isSticky={this.props.isSticky}
        hasNextRace={!!nextRace}
        hasPreviousRace={!!previousRace}
        nextRaceUrl={newNextRaceUrl}
        previousRaceUrl={newPreviousRaceUrl}
        onRaceArrowNavigationClick={() => this.props.dispatch(resetBetting())}
        isExternal={isExternal}
        // $FlowFixMe
        onPromoClick={(e: Event) => this.onPromoClick(e, isExternal, raceInfo)}
        useIsPromoTagShownFlag={this.props.useIsPromoTagShownFlag}
      />
    );
  };

  render() {
    const {
      video,
      isUserLogged,
      isControlsDisabled,
      openVideoFromMyBets,
      channel
    } = this.props;

    const shouldCloseVideo =
      this.state.changedTrack && !openVideoFromMyBets && !channel;

    if (
      !isControlsDisabled &&
      isUserLogged &&
      video.show &&
      !shouldCloseVideo
    ) {
      return this.renderVideo();
    }

    // close video if we are on a mobile view
    if (!isControlsDisabled && !openVideoFromMyBets) {
      this.props.dispatch(closeVideo());
    }

    return this.renderHeader();
  }
}

export default connect(
  (store) => ({
    video: store.RaceProgramVideo,
    isHeaderRaceNavigationShown: store.RaceProgram.showHeaderRaceNavigation,
    fixWagerSelector: store.RaceProgram.fixWagerSelector,
    isVideoButtonShown: store.RaceProgram.showVideoButtonHeader,
    isUserLogged: store.userData.logged,
    userBalance: store.userData.balance,
    enablePromosStoryblok: get(
      store,
      "capi.featureToggles.enablePromosStoryblok",
      false
    ),
    accountNumber: get(store, "userData.user.accountNumber", 0),
    isBalanceShown: get(store, "userData.preferences.balance_is_shown", "0"),
    hasPromoBadge: get(store, "capi.featureToggles.promosDisplayBadge", false),
    optedInPromos: get(store, "userData.optedInPromos", {}),
    oldPromosURLs: get(store, "capi.messages.oldPromosURLs", []),
    useIsPromoTagShownFlag: get(
      store,
      "capi.featureToggles.useIsPromoTagShownFlag",
      false
    )
  }),
  (dispatch: Dispatch<*>) => ({ dispatch })
)(RaceHeaderComponent);
